/**
 * MobileManager - Device detection and orientation management
 * Handles mobile device detection, touch capability, orientation locking,
 * and responsive scaling for the game.
 */
export class MobileManager {
  private static instance: MobileManager | null = null;
  private orientationOverlay: HTMLElement | null = null;
  private resizeCallback: (() => void) | null = null;

  private constructor() {
    // Singleton - use static methods
  }

  /**
   * Check if the device is a mobile device (phone or tablet)
   */
  public static isMobile(): boolean {
    const userAgent = navigator.userAgent || navigator.vendor;

    // Check for common mobile indicators
    const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;

    // Also check screen size as backup (tablets might not match regex)
    const isSmallScreen = window.innerWidth <= 1024 && 'ontouchstart' in window;

    return mobileRegex.test(userAgent) || isSmallScreen;
  }

  /**
   * Check if the device supports touch input
   */
  public static isTouchDevice(): boolean {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-expect-error - msMaxTouchPoints is IE/Edge specific
      navigator.msMaxTouchPoints > 0
    );
  }

  /**
   * Get the UI scale factor based on screen width
   * Returns 0.5-1.0 depending on device size
   */
  public static getScale(): number {
    const width = window.innerWidth;

    if (width < 480) return 0.5;      // Small phone
    if (width < 768) return 0.7;      // Large phone
    if (width < 1024) return 0.85;    // Tablet
    return 1.0;                        // Desktop
  }

  /**
   * Check if device is in portrait orientation
   */
  public static isPortrait(): boolean {
    return window.innerHeight > window.innerWidth;
  }

  /**
   * Check if device is in landscape orientation
   */
  public static isLandscape(): boolean {
    return window.innerWidth >= window.innerHeight;
  }

  /**
   * Attempt to lock the screen to landscape orientation
   * Shows a rotate device message if locked to portrait
   */
  public static lockLandscape(): void {
    // Try to use the Screen Orientation API if available
    // Use type assertion since TypeScript definitions may not include lock()
    const orientation = screen.orientation as ScreenOrientation & {
      lock?: (orientation: string) => Promise<void>;
    };

    if (orientation && orientation.lock) {
      orientation.lock('landscape').catch(() => {
        // Silently fail - not all browsers support this
        console.log('Orientation lock not supported');
      });
    }

    // Create or show the rotate overlay for portrait mode
    MobileManager.getInstance().createOrientationOverlay();
    MobileManager.getInstance().checkOrientation();

    // Listen for orientation changes
    window.addEventListener('resize', MobileManager.getInstance().handleResize);
    window.addEventListener('orientationchange', MobileManager.getInstance().handleResize);
  }

  /**
   * Remove orientation lock and cleanup
   */
  public static unlockOrientation(): void {
    const orientation = screen.orientation as ScreenOrientation & {
      unlock?: () => void;
    };

    if (orientation && orientation.unlock) {
      orientation.unlock();
    }

    const instance = MobileManager.getInstance();
    if (instance.orientationOverlay) {
      instance.orientationOverlay.remove();
      instance.orientationOverlay = null;
    }

    window.removeEventListener('resize', instance.handleResize);
    window.removeEventListener('orientationchange', instance.handleResize);
  }

  /**
   * Register a callback to be called on resize/orientation change
   */
  public static onResize(callback: () => void): void {
    MobileManager.getInstance().resizeCallback = callback;
  }

  /**
   * Get singleton instance
   */
  private static getInstance(): MobileManager {
    if (!MobileManager.instance) {
      MobileManager.instance = new MobileManager();
    }
    return MobileManager.instance;
  }

  /**
   * Handle resize/orientation events
   */
  private handleResize = (): void => {
    this.checkOrientation();
    if (this.resizeCallback) {
      this.resizeCallback();
    }
  };

  /**
   * Check orientation and show/hide overlay
   */
  private checkOrientation(): void {
    if (!this.orientationOverlay) return;

    if (MobileManager.isMobile() && MobileManager.isPortrait()) {
      this.orientationOverlay.style.display = 'flex';
    } else {
      this.orientationOverlay.style.display = 'none';
    }
  }

  /**
   * Create the "rotate your device" overlay
   */
  private createOrientationOverlay(): void {
    if (this.orientationOverlay) return;

    this.orientationOverlay = document.createElement('div');
    this.orientationOverlay.id = 'orientation-overlay';
    this.orientationOverlay.innerHTML = `
      <div class="orientation-content">
        <div class="rotate-icon">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <rect x="4" y="2" width="16" height="20" rx="2" ry="2"/>
            <line x1="12" y1="18" x2="12" y2="18"/>
          </svg>
          <div class="rotate-arrow">↻</div>
        </div>
        <h2>Rotate Your Device</h2>
        <p>This game is best played in landscape mode</p>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      #orientation-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #0a0a0a 0%, #1a1a2e 100%);
        display: none;
        justify-content: center;
        align-items: center;
        z-index: 10000;
        color: #00ffff;
        font-family: 'Orbitron', 'Segoe UI', sans-serif;
      }

      #orientation-overlay .orientation-content {
        text-align: center;
        padding: 20px;
      }

      #orientation-overlay .rotate-icon {
        position: relative;
        display: inline-block;
        animation: tilt 2s ease-in-out infinite;
      }

      #orientation-overlay .rotate-icon svg {
        color: #00ffff;
      }

      #orientation-overlay .rotate-arrow {
        position: absolute;
        top: -10px;
        right: -20px;
        font-size: 30px;
        animation: spin 2s linear infinite;
      }

      #orientation-overlay h2 {
        margin: 20px 0 10px 0;
        font-size: 24px;
        text-shadow: 0 0 10px #00ffff;
      }

      #orientation-overlay p {
        margin: 0;
        font-size: 14px;
        opacity: 0.8;
      }

      @keyframes tilt {
        0%, 100% { transform: rotate(0deg); }
        50% { transform: rotate(90deg); }
      }

      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(this.orientationOverlay);
  }
}
